import React, { useContext } from 'react';
import * as Yup from 'yup';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik, FormikProps } from 'formik';
import { Grid, Hidden } from '@mui/material';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { MyCPGMidYearProps, QualifyingEventPageModel } from '../mycpg-midyear/models/mycpg-midyear-epi-props';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { TcsmStates } from '../../../../root/tcsm-model';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { FormTypes } from '../../models/form-models';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { FormField } from '../../../../global/components/form-element/form-field';
import { getRequiredText } from '../../../../global/helpers/transformer';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { createEnrollmentWindow } from '../mycpg-midyear/helpers/mycpg-midyear-coverage-form-helpers';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../constants/routes';
import { DocumentUploadState } from '../personal-info/document-upload-models';
import { processRequestWithDocumentUpload } from '../personal-info/document-upload-request';
import { MyCpgMidYearPersonalInfoAlertTypes } from '../../../notification/notification-models';
import { formatDate } from '../../../../global/helpers/form-data-helper';
import { QualifyingEventType } from '../mycpg-midyear/models/mycpg-mid-year-models';
import { ArcRoutes } from '../../../arc/constants/routes';

interface QualifyingEventFormModel {
	qualifying_event: string,
	qualifying_event_date: string,
	attestation?: boolean,
}
interface Props {
	model: QualifyingEventPageModel
}

const ThirtyDaysFromNowInMilliseconds = 2592000000;
export function QualifyingEventForm(props: Props) {
	const { model } = props;
	const future = new Date();
	future.setTime(future.getTime() + ThirtyDaysFromNowInMilliseconds);
	const midYearSettings = useSelector<TcsmStates, MyCPGMidYearProps>(state => state?.epiProps?.myCpgMidyearSettingsState?.epiContent);
	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const app = useContext(DemographicsContext);
	const guid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
	const apiErrors = useApiResponseError();
	const gpsItem = useGpsType(model?.eventType as unknown as GpsType);
	const myCpgMidYearPath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.Midyear}${AccountInfoRoutes.PersonalInfoSegment}`);
	const lifeEventsPath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.LifeEvents}`);
	
	const documentUploadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);
	const dispatch = useDispatch();
	const history = useHistory();

	const getSuccessPath = (eventType: QualifyingEventType) : string => {
		switch (eventType) {
			case QualifyingEventType.loss_of_coverage:
			case QualifyingEventType.change_in_coverage_cost_or_curtailment:
			case QualifyingEventType.eligibility_change_in_medicare_medicaid_or_schp:
			case QualifyingEventType.medicare_part_d_enrollment_or_termination:
			case QualifyingEventType.enrollment_through_health_insurance_exchange:
				return useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.LifeEvents}`);				
			
			case QualifyingEventType.dependent_gaining_own_benefits:
			case QualifyingEventType.spouse_or_domestic_partner_status_change:
			default:
				return useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.RelationshipsSegment}`);
		}
	}; 

	const myCpgSucessPath = getSuccessPath(model?.eventType);

	const initialValues = {
		qualifying_event: model?.eventType,
		qualifying_event_date: null,
		attestation: false,
	};

	const validationSchema = Yup.object().shape({
		qualifying_event_date: Yup.date().nullable().max(future, midYearSettings?.moreThan30DaysError || 'Cannot be 30 days in the future').required(getRequiredText(labelSetting.requiredFormat, midYearSettings?.qualifyingEventDateLabel)),

	});

	const onSubmit = (value: QualifyingEventFormModel) => {
		const copy = {
			...value,
			qualifying_event_date: formatDate(value.qualifying_event_date),
		};
		delete copy?.attestation;

		if (documentUploadState.stagedDocuments && documentUploadState.stagedDocuments.length > 0) {
			processRequestWithDocumentUpload(
				documentUploadState,
				guid,
				dispatch,
				() => createEnrollmentWindow(
					guid,
					{
						enrollment_window: copy,
					},
					() => history.push(`${myCpgMidYearPath}?alerts=${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE},${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE_WARNING}`),
					() => history.push(`${myCpgSucessPath}?success=${FormTypes.LIFE_EVENT},${FormTypes.LIFE_EVENT_30_PLUS}`),
					dispatch,
				));
		} else {
			createEnrollmentWindow(
				guid,
				{
					enrollment_window: copy,
				},
				() => history.push(`${myCpgMidYearPath}?alerts=${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE},${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE_WARNING}`),
				() => history.push(`${myCpgSucessPath}?success=${FormTypes.LIFE_EVENT},${FormTypes.LIFE_EVENT_30_PLUS}`),
				dispatch,
			);
		}
	};

	return (
		<PageContainer>
			<MainContent >
				<Formik
					initialValues={initialValues}
					onSubmit={(values: QualifyingEventFormModel) => onSubmit(values)}
					enableReinitialize
					initialErrors={apiErrors}
					validationSchema={validationSchema}
				>
					{(formProps: FormikProps<QualifyingEventFormModel>) => (
						<>
							<Form>
								<Grid>
									<FormWrapper
										formikProps={formProps}
										submitText={midYearSettings?.continueButtonText}
										cancelText={midYearSettings?.cancelButtonText}
										onCancel={() => history.push(lifeEventsPath)}
										submitDisabled={!formProps?.values?.attestation}
										formType={model?.eventType as unknown as FormTypes}
										defaultShowDocUpload
										formKeys={Object.keys(initialValues)}
										additionalContentAboveButtons={
											<FormField
												name='attestation'
												label={model?.attestation}
												component='checkBox'
												touched={formProps.touched}
												errors={formProps.errors}
												value={formProps?.values?.attestation ?? false}
												checked={formProps?.values?.attestation ?? false}
											/>
										}
									>
										<PageHeader
											headerText={model?.pageTitle}
											description={model?.pageDescription}
										/>
										<Hidden mdUp>
											<SideBarContent>
												<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
											</SideBarContent>
										</Hidden>
										<ContentSpacer>
											<FormField
												name='qualifying_event_date'
												label={midYearSettings?.qualifyingEventDateLabel}
												component='datePicker'
												touched={formProps.touched}
												errors={formProps.errors}
												required
												autoWidth
											/>
										</ContentSpacer>

									</FormWrapper>
								</Grid>
							</Form>
						</>
					)}
				</Formik>
			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
				</SideBarContent>
			</Hidden>

		</PageContainer >

	);
}
